// src/reports/pdf/LawyerWorkloadDashboardPDF.jsx
// Reporte PDF - Dashboard de carga de trabajo del abogado
// @react-pdf/renderer + SVG (trend/donut) + barras en layout (más estable que texto en SVG)

import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
  Rect,
  Circle,
} from "@react-pdf/renderer";

import watermark from "../../assets/images/backgrounds/watermark-bg.png";

// ============================
// Helpers
// ============================
const L = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const safeArr = (arr) => (Array.isArray(arr) ? arr : []);

const safeNum = (n) => {
  const v = Number(n);
  return Number.isNaN(v) ? 0 : v;
};

const formatDateCO = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("es-CO", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const monthLabelES = (periodYYYYMM) => {
  if (!periodYYYYMM) return "—";
  const [y, m] = String(periodYYYYMM).split("-");
  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  const mm = Number(m);
  return mm >= 1 && mm <= 12 ? `${months[mm - 1]} ${y}` : String(periodYYYYMM);
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// ============================
// Tiny SVG charts (PDF)
// ============================

/**
 * Genera puntos (x,y) con escala global (min/max) para NO distorsionar series.
 */
const buildPointsScaled = (values, w, h, pad, minV, maxV) => {
  const v = safeArr(values).map(safeNum);
  if (v.length === 0) return [];

  const range = maxV - minV || 1;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  return v.map((val, i) => {
    const x = pad + innerW * (v.length === 1 ? 0 : i / (v.length - 1));
    const y = pad + innerH - ((val - minV) / range) * innerH;
    return { x, y, val };
  });
};

const buildLinePathFromPoints = (pts) => {
  if (!pts.length) return "";
  return pts
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
};

const buildAreaPathFromPoints = (pts, h, pad) => {
  if (!pts.length) return "";
  const baselineY = h - pad;
  return (
    `M ${pts[0].x.toFixed(2)} ${baselineY.toFixed(2)} ` +
    pts.map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ") +
    ` L ${pts[pts.length - 1].x.toFixed(2)} ${baselineY.toFixed(2)} Z`
  );
};

/**
 * Donut segment path (arco).
 */
const polarToCartesian = (cx, cy, r, angleDeg) => {
  const a = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const donutArcPath = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const startOuter = polarToCartesian(cx, cy, rOuter, endAngle);
  const endOuter = polarToCartesian(cx, cy, rOuter, startAngle);
  const startInner = polarToCartesian(cx, cy, rInner, startAngle);
  const endInner = polarToCartesian(cx, cy, rInner, endAngle);

  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
    "Z",
  ].join(" ");
};

// ============================
// Styles
// ============================
const COLORS = {
  opened: "#0d6efd",
  closed: "#198754",
  purple: "#6610f2",
  danger: "#dc3545",
  muted: "#6b7280",
  ink: "#111827",
  light: "#f3f4f6",
  line: "#e5e7eb",
  track: "#eef2f7",
};

const DONUT = [
  "#0d6efd",
  "#198754",
  "#ffc107",
  "#dc3545",
  "#6f42c1",
  "#20c997",
  "#0dcaf0",
  "#fd7e14",
  "#6c757d",
  "#d63384",
];

const styles = StyleSheet.create({
  page: {
    paddingTop: 26,
    paddingLeft: 26,
    paddingRight: 26,
    paddingBottom: 58,
    fontSize: 10.2,
    color: COLORS.ink,
  },

  // Watermark
  watermarkWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 0,
  },
  watermarkImg: {
    width: 420,
    opacity: 0.18,
    transform: "rotate(-18deg)",
  },
  content: { position: "relative", zIndex: 1 },

  // App bar
  appBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: COLORS.light,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: COLORS.line,
  },
  appBarTitle: { fontSize: 10.5, fontWeight: 800 },
  appBarSub: { fontSize: 9, color: "#4b5563", marginTop: 2 },
  appBarMeta: { fontSize: 8.5, color: COLORS.muted },

  h1: { fontSize: 16, fontWeight: 900, marginBottom: 2 },
  muted: { color: COLORS.muted },

  // Cards row
  cardsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
  },
  card: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 10,
    backgroundColor: "#ffffff",
    marginRight: 8,
    overflow: "hidden",
  },
  cardLast: { marginRight: 0 },

  accent: {
    position: "absolute",
    left: 0,
    top: 0,
    height: 4,
    width: "100%",
  },

  cardTitle: { fontSize: 9, color: COLORS.muted },
  cardValue: { fontSize: 14, fontWeight: 900, marginTop: 2 },
  cardHint: { fontSize: 8.5, color: COLORS.muted, marginTop: 2 },

  h2: {
    fontSize: 12.5,
    fontWeight: 900,
    color: "#0b5ed7",
    marginTop: 14,
    marginBottom: 6,
  },

  // Section blocks
  block: {
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    backgroundColor: "#ffffff",
    padding: 10,
    marginTop: 6,
  },

  blockTitle: { fontSize: 10.5, fontWeight: 900, marginBottom: 6 },

  // Tables
  tableWrap: {
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    overflow: "hidden",
    marginTop: 6,
  },
  tableHead: {
    flexDirection: "row",
    backgroundColor: COLORS.light,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.line,
  },
  th: { padding: 7, fontSize: 9, fontWeight: 900, color: COLORS.ink },
  tr: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  td: { padding: 7, fontSize: 9, color: COLORS.ink },

  // Legend
  legendRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 6 },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 10,
    marginBottom: 4,
  },
  legendDot: { width: 8, height: 8, borderRadius: 999, marginRight: 6 },

  // Bars list (Top tipos) - estable (sin texto dentro de SVG)
  barRow: { marginTop: 8 },
  barLabelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  barLabel: { fontSize: 9, color: COLORS.ink, fontWeight: 700, flex: 1 },
  barValue: {
    fontSize: 9.5,
    fontWeight: 900,
    color: COLORS.ink,
    marginLeft: 8,
  },
  barTrack: {
    marginTop: 5,
    height: 10,
    backgroundColor: COLORS.track,
    borderRadius: 999,
    overflow: "hidden",
  },
  barFill: {
    height: 10,
    borderRadius: 999,
    backgroundColor: COLORS.purple,
    opacity: 0.85,
  },

  footer: {
    position: "absolute",
    left: 26,
    right: 26,
    bottom: 16,
    fontSize: 9,
    color: COLORS.muted,
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

// ============================
// Components: Mini charts
// ============================

/**
 * Tendencia mensual (Abiertos vs Cerrados)
 * - Escala global (min=0, max=max(opened, closed))
 * - Dibuja ambas series SIEMPRE
 * - Etiquetas en puntos > 0 y también en el último punto
 */
function MiniTrendChart({ opened, closed, labels }) {
  const W = 520;
  const H = 160;
  const PAD = 18;

  const o = safeArr(opened).map(safeNum);
  const c = safeArr(closed).map(safeNum);

  const maxV = Math.max(1, ...o, ...c);
  const minV = 0;

  const oPts = buildPointsScaled(o, W, H, PAD, minV, maxV);
  const cPts = buildPointsScaled(c, W, H, PAD, minV, maxV);

  const oLine = buildLinePathFromPoints(oPts);
  const cLine = buildLinePathFromPoints(cPts);

  const oArea = buildAreaPathFromPoints(oPts, H, PAD);
  const cArea = buildAreaPathFromPoints(cPts, H, PAD);

  const gridLevels = [0, 0.5, 1]; // 0, 50%, 100% (limpio y ejecutivo)
  const lastIdx = Math.max(oPts.length, cPts.length) - 1;
  const shouldLabel = (val, idx) => val > 0 || idx === lastIdx;

  return (
    <Svg width={W} height={H}>
      {/* Marco */}
      <Rect
        x={1}
        y={1}
        width={W - 2}
        height={H - 2}
        rx={10}
        ry={10}
        fill="#ffffff"
        stroke={COLORS.line}
        strokeWidth={1}
      />

      {/* Grid + ticks Y (izquierda) */}
      {gridLevels.map((p, i) => {
        const y = PAD + (H - PAD * 2) * (1 - p);
        const v = Math.round(minV + (maxV - minV) * p);

        return (
          <React.Fragment key={i}>
            <Path
              d={`M ${PAD} ${y} L ${W - PAD} ${y}`}
              stroke={COLORS.line}
              strokeWidth={1}
              opacity={0.9}
            />
            <Text
              x={PAD - 8}
              y={y + 3}
              textAnchor="end"
              style={{ fontSize: 8, color: COLORS.muted }}
            >
              {v}
            </Text>
          </React.Fragment>
        );
      })}

      {/* Áreas (primero) */}
      {oArea ? <Path d={oArea} fill={COLORS.opened} opacity={0.1} /> : null}
      {cArea ? <Path d={cArea} fill={COLORS.closed} opacity={0.1} /> : null}

      {/* Líneas (encima) */}
      {oLine ? (
        <Path d={oLine} stroke={COLORS.opened} strokeWidth={3} fill="none" />
      ) : null}
      {cLine ? (
        <Path d={cLine} stroke={COLORS.closed} strokeWidth={3} fill="none" />
      ) : null}

      {/* Puntos + etiquetas */}
      {oPts.map((p, idx) => (
        <React.Fragment key={`o-${idx}`}>
          <Circle cx={p.x} cy={p.y} r={3.5} fill={COLORS.opened} />
          {shouldLabel(p.val, idx) ? (
            <Text
              x={p.x}
              y={p.y - 8}
              textAnchor="middle"
              style={{ fontSize: 8.5, fontWeight: 900, color: COLORS.opened }}
            >
              {p.val}
            </Text>
          ) : null}
        </React.Fragment>
      ))}

      {cPts.map((p, idx) => (
        <React.Fragment key={`c-${idx}`}>
          <Circle cx={p.x} cy={p.y} r={3.5} fill={COLORS.closed} />
          {shouldLabel(p.val, idx) ? (
            <Text
              x={p.x}
              y={p.y + 14}
              textAnchor="middle"
              style={{ fontSize: 8.5, fontWeight: 900, color: COLORS.closed }}
            >
              {p.val}
            </Text>
          ) : null}
        </React.Fragment>
      ))}

      {/* Labels X (primero y último) */}
      {labels?.length ? (
        <>
          <Text
            x={PAD}
            y={H - 6}
            textAnchor="start"
            style={{ fontSize: 8, color: COLORS.muted }}
          >
            {labels[0]}
          </Text>
          <Text
            x={W - PAD}
            y={H - 6}
            textAnchor="end"
            style={{ fontSize: 8, color: COLORS.muted }}
          >
            {labels[labels.length - 1]}
          </Text>
        </>
      ) : null}
    </Svg>
  );
}

/**
 * Top tipos de caso (abiertos en rango)

 * - NO usamos texto dentro de SVG (eso es lo que te estaba haciendo que solo apareciera 1 barra).
 * - Renderizamos una lista de barras con layout normal (View) => estable siempre.
 */
function MiniBarList({ items }) {
  const rows = safeArr(items)
    .map((x) => ({
      key: L(x?.key),
      value: safeNum(x?.value),
    }))
    .sort((a, b) => b.value - a.value || a.key.localeCompare(b.key))
    .slice(0, 8);

  const max = Math.max(1, ...rows.map((x) => x.value));

  return (
    <View>
      {rows.map((it, idx) => {
        const pct = it.value / max;
        const fillW = `${Math.round(clamp(pct, 0, 1) * 100)}%`;

        return (
          <View key={`${it.key}-${idx}`} style={styles.barRow} wrap={false}>
            <View style={styles.barLabelRow}>
              <Text style={styles.barLabel}>{it.key}</Text>
              <Text style={styles.barValue}>{it.value}</Text>
            </View>

            <View style={styles.barTrack}>
              <View style={[styles.barFill, { width: fillW }]} />
            </View>
          </View>
        );
      })}
    </View>
  );
}

function MiniDonut({ items }) {
  const rows = safeArr(items);
  const total = rows.reduce((acc, x) => acc + safeNum(x?.value), 0) || 1;

  const size = 140;
  const cx = 70;
  const cy = 70;
  const rOuter = 56;
  const rInner = 34;

  let start = 0;

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <Svg width={size} height={size}>
        {rows.slice(0, 8).map((it, idx) => {
          const val = safeNum(it?.value);
          const angle = (val / total) * 360;
          const end = start + angle;

          const path = donutArcPath(cx, cy, rOuter, rInner, start, end);
          const fill = DONUT[idx % DONUT.length];

          start = end;

          return <Path key={idx} d={path} fill={fill} />;
        })}

        <Circle cx={cx} cy={cy} r={rInner - 6} fill="#ffffff" />
        <Text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          style={{ fontSize: 10.5, fontWeight: 900 }}
        >
          {total}
        </Text>
        <Text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          style={{ fontSize: 8.5, color: COLORS.muted }}
        >
          activos
        </Text>
      </Svg>

      <View style={{ marginLeft: 10, flex: 1 }}>
        <Text style={{ fontSize: 10.5, fontWeight: 900, marginBottom: 4 }}>
          Estados (activos)
        </Text>
        <View style={styles.legendRow}>
          {rows.slice(0, 8).map((it, idx) => (
            <View key={idx} style={styles.legendItem}>
              <View
                style={[
                  styles.legendDot,
                  { backgroundColor: DONUT[idx % DONUT.length] },
                ]}
              />
              <Text style={{ fontSize: 9, color: COLORS.ink }}>
                {L(it?.key)} ({safeNum(it?.value)})
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

// ============================
// Main
// ============================
export default function LawyerWorkloadDashboardPDF({ data }) {
  const d = data || {};

  const trend = safeArr(d.trendByMonth);
  const status = safeArr(d.statusDistributionNow);
  const types = safeArr(d.caseTypeDistributionInRange);
  const recent = safeArr(d.recentCases);

  const totalEver = safeNum(d.totalAssignedEver);
  const activeNow = safeNum(d.activeNow);
  const closedEver = safeNum(d.closedEver);
  const openedRange = safeNum(d.openedInRange);
  const closedRange = safeNum(d.closedInRange);
  const deadlineActive = safeNum(d.deadlineBasedActiveNow);
  const overdueNow = safeNum(d.overdueNow);

  const closeRateEver = totalEver > 0 ? (closedEver / totalEver) * 100 : 0;
  const closeRateRange =
    openedRange > 0 ? (closedRange / openedRange) * 100 : 0;
  const overduePct =
    deadlineActive > 0 ? (overdueNow / deadlineActive) * 100 : 0;

  const openedSeries = trend.map((x) => safeNum(x?.opened));
  const closedSeries = trend.map((x) => safeNum(x?.closed));
  const trendLabels = trend.map((t) => monthLabelES(t?.period));

  const generatedAt = new Date().toLocaleString("es-CO", {
    timeZone: "America/Bogota",
  });

  // Tabla tendencia: últimos 12 periodos para consistencia
  const trendTableRows = trend.length > 12 ? trend.slice(-12) : trend;

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Watermark */}
        <View style={styles.watermarkWrap} fixed>
          <Image src={watermark} style={styles.watermarkImg} />
        </View>

        <View style={styles.content}>
          {/* APP BAR */}
          <View style={styles.appBar}>
            <View style={{ flex: 1 }}>
              <Text style={styles.appBarTitle}>
                NovianCore · Reporte gerencial
              </Text>
              <Text style={styles.appBarSub}>
                Dashboard de carga por abogado · {L(d.lawyerName)}
              </Text>
            </View>
            <View style={{ alignItems: "flex-end" }}>
              <Text style={styles.appBarMeta}>Generado: {generatedAt}</Text>
              <Text style={styles.appBarMeta}>TZ: America/Bogota</Text>
            </View>
          </View>

          {/* Header */}
          <Text style={styles.h1}>Carga de trabajo del abogado</Text>
          <Text style={styles.muted}>
            Abogado: {L(d.lawyerName)} · Rango: {formatDateCO(d.from)} -{" "}
            {formatDateCO(d.to)}
          </Text>

          {/* Summary cards */}
          <View style={styles.cardsRow}>
            <View style={styles.card}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.opened }]}
              />
              <Text style={styles.cardTitle}>Histórico asignados</Text>
              <Text style={styles.cardValue}>{totalEver}</Text>
              <Text style={styles.cardHint}>
                Total de casos históricos asignados.
              </Text>
            </View>

            <View style={styles.card}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.closed }]}
              />
              <Text style={styles.cardTitle}>Activos hoy</Text>
              <Text style={styles.cardValue}>{activeNow}</Text>
              <Text style={styles.cardHint}>
                Activos y sin fecha de cierre.
              </Text>
            </View>

            <View style={[styles.card, styles.cardLast]}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.danger }]}
              />
              <Text style={styles.cardTitle}>Riesgo</Text>
              <Text style={styles.cardValue}>{overdueNow} vencidos</Text>
              <Text style={styles.cardHint}>
                Casos con fecha límite activos: {deadlineActive} · Riesgo:{" "}
                {Math.round(overduePct)}%
              </Text>
            </View>
          </View>

          {/* KPIs row 2 */}
          <View style={styles.cardsRow}>
            <View style={styles.card}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.purple }]}
              />
              <Text style={styles.cardTitle}>Abiertos en rango</Text>
              <Text style={styles.cardValue}>{openedRange}</Text>
              <Text style={styles.cardHint}>
                Casos creados dentro del rango.
              </Text>
            </View>

            <View style={styles.card}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.closed }]}
              />
              <Text style={styles.cardTitle}>Cerrados en rango</Text>
              <Text style={styles.cardValue}>{closedRange}</Text>
              <Text style={styles.cardHint}>
                Tasa: {Math.round(closeRateRange)}%
              </Text>
            </View>

            <View style={[styles.card, styles.cardLast]}>
              <View
                style={[styles.accent, { backgroundColor: COLORS.opened }]}
              />
              <Text style={styles.cardTitle}>Tasa de cierre (histórico)</Text>
              <Text style={styles.cardValue}>{Math.round(closeRateEver)}%</Text>
              <Text style={styles.cardHint}>
                Cerrados históricos: {closedEver} / {totalEver}
              </Text>
            </View>
          </View>

          {/* 1. Trend */}
          <Text style={styles.h2}>
            1. Tendencia mensual (Abiertos vs Cerrados)
          </Text>

          <View style={styles.block}>
            <Text style={styles.blockTitle}>Evolución mensual</Text>

            {trend.length === 0 ? (
              <Text style={styles.muted}>
                Sin datos de tendencia para el rango seleccionado.
              </Text>
            ) : (
              <>
                <MiniTrendChart
                  opened={openedSeries}
                  closed={closedSeries}
                  labels={trendLabels}
                />

                <View style={styles.legendRow}>
                  <View style={styles.legendItem}>
                    <View
                      style={[
                        styles.legendDot,
                        { backgroundColor: COLORS.opened },
                      ]}
                    />
                    <Text style={{ fontSize: 9 }}>Abiertos</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View
                      style={[
                        styles.legendDot,
                        { backgroundColor: COLORS.closed },
                      ]}
                    />
                    <Text style={{ fontSize: 9 }}>Cerrados</Text>
                  </View>
                </View>

                {/* Tabla de apoyo: últimos 12 */}
                <View style={{ marginTop: 8 }}>
                  <View style={styles.tableHead}>
                    <Text style={[styles.th, { width: "40%" }]}>Periodo</Text>
                    <Text
                      style={[styles.th, { width: "30%", textAlign: "right" }]}
                    >
                      Abiertos
                    </Text>
                    <Text
                      style={[styles.th, { width: "30%", textAlign: "right" }]}
                    >
                      Cerrados
                    </Text>
                  </View>

                  {trendTableRows.map((t, idx) => (
                    <View key={idx} style={styles.tr}>
                      <Text style={[styles.td, { width: "40%" }]}>
                        {monthLabelES(t?.period)}
                      </Text>
                      <Text
                        style={[
                          styles.td,
                          { width: "30%", textAlign: "right" },
                        ]}
                      >
                        {safeNum(t?.opened)}
                      </Text>
                      <Text
                        style={[
                          styles.td,
                          { width: "30%", textAlign: "right" },
                        ]}
                      >
                        {safeNum(t?.closed)}
                      </Text>
                    </View>
                  ))}
                </View>
              </>
            )}
          </View>

          {/* 2. Distributions */}
          <Text style={styles.h2}>2. Distribución por estado y tipo</Text>

          <View style={styles.block}>
            {status.length === 0 ? (
              <Text style={styles.muted}>
                Sin estados activos para mostrar.
              </Text>
            ) : (
              <MiniDonut items={status} />
            )}
          </View>

          <View style={styles.block}>
            <Text style={styles.blockTitle}>
              Top tipos de caso (abiertos en rango)
            </Text>

            {types.length === 0 ? (
              <Text style={styles.muted}>Sin tipos de caso en el rango.</Text>
            ) : (
              <MiniBarList items={types} />
            )}
          </View>

          {/* 3. Recent cases */}
          <Text style={styles.h2}>3. Casos recientes</Text>
          <View style={styles.tableWrap}>
            <View style={styles.tableHead}>
              <Text style={[styles.th, { width: "16%" }]}>Código</Text>
              <Text style={[styles.th, { width: "44%" }]}>Título</Text>
              <Text style={[styles.th, { width: "20%" }]}>Estado</Text>
              <Text style={[styles.th, { width: "20%" }]}>Fecha límite</Text>
            </View>

            {recent.length === 0 ? (
              <View style={styles.tr}>
                <Text
                  style={[styles.td, { width: "100%", color: COLORS.muted }]}
                >
                  No hay casos recientes para mostrar.
                </Text>
              </View>
            ) : (
              recent.slice(0, 10).map((c, idx) => (
                <View key={c?.id || idx} style={styles.tr}>
                  <Text style={[styles.td, { width: "16%" }]}>
                    {L(c?.internalCode)}
                  </Text>
                  <Text style={[styles.td, { width: "44%" }]}>
                    {L(c?.title)}
                  </Text>
                  <Text style={[styles.td, { width: "20%" }]}>
                    {L(c?.caseStatusName)}
                  </Text>
                  <Text style={[styles.td, { width: "20%" }]}>
                    {c?.isDeadlineBasedCase
                      ? formatDateCO(c?.responseDeadline)
                      : "—"}
                  </Text>
                </View>
              ))
            )}
          </View>

          <Text style={[styles.muted, { marginTop: 10, fontSize: 9 }]}>
            Nota: “Casos con fecha límite activos” = casos activos que manejan
            una fecha límite de respuesta/gestión.
          </Text>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text>NovianCore · Reporte gerencial · {generatedAt}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
